import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useAuth } from "react-oidc-context";
import { getRewardTypeInfo } from "./utils/rewardTypeUtils";

const API_URL = process.env.REACT_APP_API_URL || "/api";
const IMAGE_SERVICE_URL = process.env.REACT_APP_IMAGE_SERVICE_URL || "/api-images";
const IMAGE_API_TOKEN = process.env.REACT_APP_IMAGE_API_TOKEN || "";
const isLocalDev = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
const CDN_LOCAL_BASE = "http://localhost:8090/images";
const CDN_STAGING_BASE = "https://cdn-staging.luniwave.com/images";
const CDN_PROD_BASE = "https://cdn.luniwave.com/images";

const getMappedCdnBaseUrl = () => {
  const host = window.location.hostname.toLowerCase();
  if (host === "localhost" || host === "127.0.0.1") return CDN_LOCAL_BASE;
  if (host.includes("preprod") || host.includes("staging")) return CDN_STAGING_BASE;
  return CDN_PROD_BASE;
};

const extractFilenameFromUrl = (imageUrl) => {
  if (!imageUrl) return "";
  try {
    const parsedUrl = new URL(imageUrl, window.location.origin);
    const filename = parsedUrl.pathname.split("/").filter(Boolean).pop();
    return filename ? decodeURIComponent(filename) : "";
  } catch (err) {
    const filename = String(imageUrl).split("?")[0].split("/").filter(Boolean).pop();
    return filename ? decodeURIComponent(filename) : "";
  }
};

/**
 * Helper pour obtenir le nom d'une langue
 */
const getLanguageName = (code) => {
  const languageNames = {
    fr: "Français",
    en: "English",
    es: "Español",
    de: "Deutsch",
    it: "Italiano",
    pt: "Português",
    nl: "Nederlands",
    pl: "Polski",
    ru: "Русский",
    zh: "中文",
    ja: "日本語",
    ko: "한국어",
    ar: "العربية",
  };
  return languageNames[code] || code.toUpperCase();
};

/**
 * Formulaire simplifié en une seule page pour créer une récompense
 * Version 2 - Repart de zéro avec une approche simple et intuitive
 * Gère tous les types de récompenses avec leurs spécificités
 */
function SimpleRewardFormV2({ hotelId, rewardType, rewardId, onSuccess, onCancel }) {
  const auth = useAuth();
  const token = auth.user?.access_token;

  const typeInfo = getRewardTypeInfo(rewardType);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(!!rewardId);
  const [availableLanguages, setAvailableLanguages] = useState([]);
  const [loadingLanguages, setLoadingLanguages] = useState(true);
  const [errors, setErrors] = useState({});
  const isEditMode = !!rewardId;

  // État du formulaire
  const [formData, setFormData] = useState({
    // Descriptions multilingues (FR et EN par défaut)
    descriptions: [
      {
        language: "fr",
        name: "",
        subtitle: "",
        description: "",
      },
      {
        language: "en",
        name: "",
        subtitle: "",
        description: "",
      },
    ],

    // Informations de base
    imageUrl: "",
    iconUrl: "",
    cost: 5,

    // Volume d'eau (pour type EAU)
    waterVolume: 0,
  });

  // Gestion CDN d'images
  const [cdnImages, setCdnImages] = useState([]);
  const [loadingCdnImages, setLoadingCdnImages] = useState(false);
  const [cdnImageSearch, setCdnImageSearch] = useState("");
  const [cdnUploadFile, setCdnUploadFile] = useState(null);
  const [cdnUploadName, setCdnUploadName] = useState("");
  const [uploadingToCdn, setUploadingToCdn] = useState(false);
  const [imageLibraryMessage, setImageLibraryMessage] = useState("");
  const [imageLibraryMessageType, setImageLibraryMessageType] = useState("info");
  const [cdnTargetField, setCdnTargetField] = useState("iconUrl");

  const imageApiHeaders = useMemo(() => {
    if (isLocalDev && IMAGE_API_TOKEN) {
      return { "x-api-token": IMAGE_API_TOKEN };
    }
    return {};
  }, []);

  const fetchImageService = useCallback(
    async (path, options = {}) => {
      const withTokenHeaders = { ...(options.headers || {}), ...imageApiHeaders };

      let response = await fetch(`${IMAGE_SERVICE_URL}${path}`, {
        ...options,
        headers: withTokenHeaders,
      });

      // Fallback si token invalide: retenter sans header
      if (response.status === 401 && imageApiHeaders["x-api-token"]) {
        response = await fetch(`${IMAGE_SERVICE_URL}${path}`, {
          ...options,
          headers: options.headers || {},
        });
      }

      return response;
    },
    [imageApiHeaders]
  );

  const buildImageUrl = useCallback((filename) => {
    return `${getMappedCdnBaseUrl()}/${encodeURIComponent(filename)}`;
  }, []);

  const refreshCdnImages = useCallback(async () => {
    setLoadingCdnImages(true);
    try {
      const res = await fetchImageService("/uploads");
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || data.message || "Erreur de chargement des images");
      }
      setCdnImages(data.files || []);
    } catch (err) {
      setImageLibraryMessageType("error");
      setImageLibraryMessage(err.message || "Erreur de chargement des images");
    } finally {
      setLoadingCdnImages(false);
    }
  }, [fetchImageService]);

  const handleUploadToCdn = useCallback(async () => {
    if (!cdnUploadFile) return;

    setUploadingToCdn(true);
    setImageLibraryMessageType("info");
    setImageLibraryMessage("Upload en cours...");

    try {
      const formDataUpload = new FormData();
      formDataUpload.append("image", cdnUploadFile);
      if (cdnUploadName.trim()) {
        formDataUpload.append("name", cdnUploadName.trim());
      }

      const res = await fetchImageService("/upload", {
        method: "POST",
        body: formDataUpload,
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || data.message || "Erreur upload image");
      }

      const uploadedFilename = data.newFilename || data.filename || extractFilenameFromUrl(data.imageUrl);
      if (uploadedFilename) {
        setFormData((prev) => ({
          ...prev,
          [cdnTargetField]: buildImageUrl(uploadedFilename),
        }));
      } else if (data.imageUrl) {
        setFormData((prev) => ({
          ...prev,
          [cdnTargetField]: data.imageUrl,
        }));
      }

      setCdnUploadFile(null);
      setCdnUploadName("");
      setImageLibraryMessageType("success");
      setImageLibraryMessage(cdnTargetField === "iconUrl" ? "Icône uploadée et bibliothèque mise à jour." : "Image uploadée et bibliothèque mise à jour.");
      await refreshCdnImages();
    } catch (err) {
      setImageLibraryMessageType("error");
      setImageLibraryMessage(err.message || "Erreur upload image");
    } finally {
      setUploadingToCdn(false);
    }
  }, [cdnTargetField, cdnUploadFile, cdnUploadName, fetchImageService, refreshCdnImages]);

  useEffect(() => {
    refreshCdnImages();
  }, [refreshCdnImages]);

  const imageFiles = useMemo(
    () => cdnImages.filter((filename) => /\.(png|jpe?g|webp|gif|bmp|svg|avif)$/i.test(filename)),
    [cdnImages]
  );

  const filteredImageFiles = useMemo(() => {
    if (!cdnImageSearch.trim()) return imageFiles;
    const search = cdnImageSearch.trim().toLowerCase();
    return imageFiles.filter((filename) => filename.toLowerCase().includes(search));
  }, [cdnImageSearch, imageFiles]);

  // Charger les langues disponibles
  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        const response = await fetch(`${API_URL}/greenmiles/languages`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const data = await response.json();
        if (data.success === "OK") {
          setAvailableLanguages(data.result || []);
        }
      } catch (err) {
        console.error("Erreur lors du chargement des langues:", err);
      } finally {
        setLoadingLanguages(false);
      }
    };

    if (token) {
      fetchLanguages();
    }
  }, [token]);

  // Charger les données de la récompense si on est en mode édition
  useEffect(() => {
    const fetchRewardData = async () => {
      if (!rewardId || !token) return;

      try {
        setLoadingData(true);
        const response = await fetch(`${API_URL}/greenmiles/hotels/${hotelId}/rewards/${rewardId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const data = await response.json();
        if (data.success === "OK") {
          const reward = data.result;

          // Préparer les données du formulaire
          const formattedData = {
            descriptions: (reward.descriptions || []).map((desc) => ({
              language: desc.language,
              name: desc.name || "",
              subtitle: desc.subtitle || "",
              description: desc.description || "",
            })),
            imageUrl: reward.rwPictureURL || "",
            iconUrl: reward.rwIconURL || "",
            cost: reward.rwCost || 5,
            waterVolume: reward.rwWaterVolume || 0,
          };

          // S'assurer qu'il y a au moins une description
          if (formattedData.descriptions.length === 0) {
            formattedData.descriptions = [
              {
                language: "fr",
                name: "",
                subtitle: "",
                description: "",
              },
            ];
          }

          setFormData(formattedData);
        } else {
          setErrors({ submit: data.message || "Erreur lors du chargement de la récompense" });
        }
      } catch (err) {
        console.error("Erreur lors du chargement de la récompense:", err);
        setErrors({ submit: "Erreur lors du chargement de la récompense" });
      } finally {
        setLoadingData(false);
      }
    };

    if (rewardId && token) {
      fetchRewardData();
    }
  }, [rewardId, hotelId, token]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Gestion des descriptions multilingues
  const handleDescriptionChange = (index, field, value) => {
    const newDescriptions = [...formData.descriptions];
    newDescriptions[index] = {
      ...newDescriptions[index],
      [field]: value,
    };
    setFormData((prev) => ({
      ...prev,
      descriptions: newDescriptions,
    }));
  };

  const addDescription = () => {
    // Trouver une langue disponible qui n'est pas déjà utilisée
    const usedLanguages = formData.descriptions.map((d) => d.language);
    const nextLanguage = availableLanguages.find((l) => !usedLanguages.includes(l.language)) || (availableLanguages.length > 0 ? availableLanguages[0] : { language: "en" });

    const newDescription = {
      language: nextLanguage.language || "en",
      name: "",
      subtitle: "",
      description: "",
    };
    setFormData((prev) => ({
      ...prev,
      descriptions: [...prev.descriptions, newDescription],
    }));
  };

  const removeDescription = (index) => {
    if (formData.descriptions.length > 1) {
      const newDescriptions = formData.descriptions.filter((_, i) => i !== index);
      setFormData((prev) => ({
        ...prev,
        descriptions: newDescriptions,
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Validation des descriptions
    const frenchDesc = formData.descriptions.find((d) => d.language === "fr");
    if (!frenchDesc?.name?.trim()) {
      newErrors.descriptions = "Le nom en français est obligatoire";
    } else if (!frenchDesc?.subtitle?.trim()) {
      newErrors.descriptions = "Le sous-titre en français est obligatoire";
    }
    if (!frenchDesc?.description?.trim()) {
      newErrors.descriptions = "La description en français est obligatoire";
    }

    // Validation du coût
    if (formData.cost === "" || formData.cost === null || formData.cost === undefined) {
      newErrors.cost = "Le coût est obligatoire";
    } else if (Number(formData.cost) < 0) {
      newErrors.cost = "Le coût doit être supérieur ou égal à 0";
    }

    // Validation de l'image
    if (!formData.imageUrl?.trim()) {
      newErrors.imageUrl = "L'URL de l'image est obligatoire";
    } else {
      try {
        new URL(formData.imageUrl.trim());
      } catch (e) {
        newErrors.imageUrl = "L'URL doit être valide";
      }
    }

    if (!formData.iconUrl?.trim()) {
      newErrors.iconUrl = "L'URL de l'icône est obligatoire";
    } else {
      try {
        new URL(formData.iconUrl.trim());
      } catch (e) {
        newErrors.iconUrl = "L'URL de l'icône doit être valide";
      }
    }

    // Validation du volume d'eau pour EAU
    if (rewardType === "EAU") {
      if (formData.waterVolume === "" || formData.waterVolume === null || formData.waterVolume === undefined) {
        newErrors.waterVolume = "Le volume d'eau est obligatoire pour les récompenses EAU";
      } else if (Number(formData.waterVolume) < 0) {
        newErrors.waterVolume = "Le volume d'eau doit être supérieur ou égal à 0";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      // Générer rwName à partir du nom français
      const frenchDesc = formData.descriptions.find((d) => d.language === "fr");
      const rwName = frenchDesc?.name?.trim() || "";

      // Préparer le payload
      const payload = {
        rwName,
        rwType: rewardType,
        rwCost: Number(formData.cost),
        rwPictureURL: formData.imageUrl.trim(),
        rwIconURL: formData.iconUrl.trim() || null,
        descriptions: formData.descriptions.map((desc) => ({
          language: desc.language,
          name: desc.name.trim(),
          subtitle: (desc.subtitle || "").trim(),
          description: desc.description.trim(),
        })),
      };

      // Ajouter le volume d'eau pour EAU
      if (rewardType === "EAU") {
        payload.rwWaterVolume = Number(formData.waterVolume);
      }

      const url = isEditMode ? `${API_URL}/greenmiles/hotels/${hotelId}/rewards/${rewardId}` : `${API_URL}/greenmiles/hotels/${hotelId}/rewards`;

      const response = await fetch(url, {
        method: isEditMode ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (data.success === "OK") {
        onSuccess?.();
      } else {
        setErrors({
          submit: data.message || (isEditMode ? "Erreur lors de la modification de la récompense" : "Erreur lors de la création de la récompense"),
        });
      }
    } catch (err) {
      console.error(`Erreur lors de ${isEditMode ? "la modification" : "la création"}:`, err);
      setErrors({
        submit: isEditMode ? "Erreur lors de la modification de la récompense" : "Erreur lors de la création de la récompense",
      });
    } finally {
      setLoading(false);
    }
  };

  if (loadingLanguages || loadingData) {
    return <div className="text-center py-8">Chargement...</div>;
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* En-tête avec info sur le type */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-green-800 mb-2">🎁 {typeInfo?.displayName || rewardType}</h3>
        <p className="text-sm text-green-700">{typeInfo?.description || ""}</p>
      </div>

      {/* Erreur générale */}
      {errors.submit && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-sm text-red-800">{errors.submit}</p>
        </div>
      )}

      {/* Descriptions multilingues */}
      <div className="border-t border-gray-200 pt-4">
        <div className="flex justify-between items-center mb-4">
          <h4 className="text-sm font-medium text-gray-700">Descriptions multilingues</h4>
          <button type="button" onClick={addDescription} className="text-sm text-green-600 hover:text-green-700 font-medium">
            + Ajouter une langue
          </button>
        </div>

        <div className="space-y-4">
          {formData.descriptions.map((desc, index) => (
            <div key={index} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <div className="flex justify-between items-center mb-3">
                <h5 className="text-sm font-medium text-gray-800">
                  Description {index + 1} - {getLanguageName(desc.language)}
                </h5>
                {formData.descriptions.length > 1 && (
                  <button type="button" onClick={() => removeDescription(index)} className="text-xs text-red-600 hover:text-red-800">
                    Supprimer
                  </button>
                )}
              </div>

              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Langue <span className="text-red-500">*</span>
                  </label>
                  <select value={desc.language} onChange={(e) => handleDescriptionChange(index, "language", e.target.value)} className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500">
                    {availableLanguages.map((lang) => (
                      <option key={lang.language} value={lang.language}>
                        {lang.name || getLanguageName(lang.language)}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Nom affiché <span className="text-red-500">*</span>
                  </label>
                  <input type="text" value={desc.name} onChange={(e) => handleDescriptionChange(index, "name", e.target.value)} className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500" placeholder="Ex: Code de réduction 10%" />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Sous-titre de la card <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={desc.subtitle ?? ""}
                    onChange={(e) => handleDescriptionChange(index, "subtitle", e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500"
                    placeholder="Ex: Réduisez votre empreinte carbone"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Description <span className="text-red-500">*</span>
                  </label>
                  <textarea value={desc.description} onChange={(e) => handleDescriptionChange(index, "description", e.target.value)} rows={3} className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500" placeholder="Décrivez la récompense..." />
                </div>
              </div>
            </div>
          ))}
        </div>

        {errors.descriptions && <p className="text-xs text-red-500 mt-1">{errors.descriptions}</p>}
      </div>

      {/* Informations complémentaires */}
      <div className="border-t border-gray-200 pt-4">
        <h4 className="text-sm font-medium text-gray-700 mb-3">Informations complémentaires</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="cost" className="block text-sm font-medium text-gray-700 mb-1">
              Coût (GreenMiles) <span className="text-red-500">*</span>
            </label>
            <input type="number" id="cost" min="0" max="10000" value={formData.cost} onChange={(e) => handleChange("cost", e.target.value)} className={`w-full p-2 border rounded-md focus:ring-2 focus:ring-green-500 ${errors.cost ? "border-red-500" : "border-gray-300"}`} />
            {errors.cost && <p className="text-xs text-red-500 mt-1">{errors.cost}</p>}
          </div>

          <div>
            <label htmlFor="imageUrl" className="block text-sm font-medium text-gray-700 mb-1">
              URL de l'image <span className="text-red-500">*</span>
            </label>
            <input type="url" id="imageUrl" value={formData.imageUrl} onChange={(e) => handleChange("imageUrl", e.target.value)} className={`w-full p-2 border rounded-md focus:ring-2 focus:ring-green-500 ${errors.imageUrl ? "border-red-500" : "border-gray-300"}`} placeholder="https://..." />
            {/* Aperçu de l'image */}
            {formData.imageUrl && (
              <div className="mt-2">
                <img
                  src={formData.imageUrl}
                  alt="Aperçu"
                  className="w-32 h-32 object-cover rounded-lg border border-gray-200"
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
              </div>
            )}
            {errors.imageUrl && <p className="text-xs text-red-500 mt-1">{errors.imageUrl}</p>}
          </div>

          <div className="md:col-span-2">
            <label htmlFor="iconUrl" className="block text-sm font-medium text-gray-700 mb-1">
              URL de l'icône <span className="text-red-500">*</span>
            </label>
            <input type="url" id="iconUrl" value={formData.iconUrl} onChange={(e) => handleChange("iconUrl", e.target.value)} className={`w-full p-2 border rounded-md focus:ring-2 focus:ring-green-500 ${errors.iconUrl ? "border-red-500" : "border-gray-300"}`} placeholder="https://..." />
            {formData.iconUrl && (
              <div className="mt-2">
                <img
                  src={formData.iconUrl}
                  alt="Aperçu icône"
                  className="w-24 h-24 object-contain rounded-lg border border-gray-200 bg-white p-1"
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
              </div>
            )}
            {errors.iconUrl && <p className="text-xs text-red-500 mt-1">{errors.iconUrl}</p>}
          </div>
        </div>

        {/* CDN d'images */}
        <div className="mt-4 border border-gray-200 rounded-md p-4 bg-gray-50 space-y-3">
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm font-medium text-gray-700">CDN d'images</p>
            <button type="button" onClick={refreshCdnImages} className="text-xs underline text-[#365644] whitespace-nowrap">
              Rafraîchir
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
            <span className="text-gray-600">Appliquer à :</span>
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input type="radio" name="rewardCdnTargetField" checked={cdnTargetField === "imageUrl"} onChange={() => setCdnTargetField("imageUrl")} />
              Image principale
            </label>
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input type="radio" name="rewardCdnTargetField" checked={cdnTargetField === "iconUrl"} onChange={() => setCdnTargetField("iconUrl")} />
              Icône (nouvelle carte)
            </label>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[1fr,1fr,auto] gap-2">
            <input type="file" accept="image/*" onChange={(e) => setCdnUploadFile(e.target.files?.[0] || null)} className="block w-full text-xs text-gray-700" />
            <input type="text" value={cdnUploadName} onChange={(e) => setCdnUploadName(e.target.value)} placeholder="Nom personnalisé (optionnel)" className="w-full p-2 border border-gray-300 rounded-md text-sm" />
            <button type="button" onClick={handleUploadToCdn} disabled={!cdnUploadFile || uploadingToCdn} className="px-4 py-2 text-sm bg-[#365644] text-white rounded-md hover:bg-[#2d4c3c] disabled:opacity-60 disabled:cursor-not-allowed">
              {uploadingToCdn ? "Upload..." : "Uploader"}
            </button>
          </div>

          <div className="flex items-center gap-2">
            <input type="text" value={cdnImageSearch} onChange={(e) => setCdnImageSearch(e.target.value)} placeholder="Rechercher une image du CDN..." className="w-full p-2 border border-gray-300 rounded-md text-sm" />
            <span className="text-xs text-gray-500 whitespace-nowrap">{filteredImageFiles.length} résultat(s)</span>
          </div>

          {imageLibraryMessage && (
            <p className={`text-xs ${imageLibraryMessageType === "error" ? "text-red-600" : imageLibraryMessageType === "success" ? "text-green-700" : "text-gray-600"}`}>
              {imageLibraryMessage}
            </p>
          )}

          {loadingCdnImages ? (
            <p className="text-xs text-gray-500">Chargement des images...</p>
          ) : filteredImageFiles.length === 0 ? (
            <p className="text-xs text-gray-500">Aucune image disponible.</p>
          ) : (
            <div className="max-h-64 overflow-y-auto pr-1">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                {filteredImageFiles.map((filename) => {
                  const imageUrl = buildImageUrl(filename);
                  const isSelected = formData[cdnTargetField] === imageUrl;
                  return (
                    <button
                      key={filename}
                      type="button"
                      onClick={() => {
                        handleChange(cdnTargetField, imageUrl);
                        setImageLibraryMessageType("success");
                        setImageLibraryMessage(cdnTargetField === "iconUrl" ? "Icône sélectionnée." : "Image sélectionnée.");
                      }}
                      className={`text-left border rounded-md overflow-hidden bg-white hover:border-[#365644] ${isSelected ? "border-[#365644] ring-1 ring-[#365644]" : "border-gray-200"}`}
                    >
                      <img src={imageUrl} alt={filename} className="w-full h-20 object-contain bg-white p-1" loading="lazy" />
                      <p className="px-2 py-1 text-[11px] text-gray-700 truncate">{filename}</p>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Volume d'eau pour EAU */}
        {rewardType === "EAU" && (
          <div className="mt-4">
            <label htmlFor="waterVolume" className="block text-sm font-medium text-gray-700 mb-1">
              Volume d'eau (litres) <span className="text-red-500">*</span>
            </label>
            <input type="number" id="waterVolume" min="0" value={formData.waterVolume} onChange={(e) => handleChange("waterVolume", e.target.value)} className={`w-full p-2 border rounded-md focus:ring-2 focus:ring-green-500 ${errors.waterVolume ? "border-red-500" : "border-gray-300"}`} placeholder="Ex: 100" />
            <p className="text-xs text-gray-500 mt-1">Volume d'eau en litres qui sera donné à une association caritative</p>
            {errors.waterVolume && <p className="text-xs text-red-500 mt-1">{errors.waterVolume}</p>}
          </div>
        )}
      </div>

      {/* Boutons */}
      <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
        <button type="button" onClick={onCancel} className="px-4 py-2 text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors">
          Annuler
        </button>
        <button type="submit" disabled={loading} className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
          {loading ? (isEditMode ? "Modification..." : "Création...") : isEditMode ? "Modifier la récompense" : "Créer la récompense"}
        </button>
      </div>
    </form>
  );
}

export default SimpleRewardFormV2;
